<script setup lang="ts">
  import { authSession } from "@/authentication/authenticationService"
  import { useSwitchAccount } from "@/lib/shared/useSwitchAccount"
  import { useAnalyticsTracking } from "@/lib/useAnalyticsTracking"
  import useAuthErrorDialog from "@/lib/useAuthErrorDialog"
  import { initSoChat, isSoChatEnabled } from "@/plugins/soChat.ts"
  import { useQueryClient } from "@tanstack/vue-query"
  import { useColorMode } from "@vueuse/core"
  import { computed, ref, watchEffect } from "vue"
  import { START_LOCATION, useRoute, useRouter } from "vue-router"
  import AuthErrorDialog from "@/components/AuthErrorDialog.vue"
  import SwitchAccountModal from "@/components/dialogs/SwitchAccountModal.vue"
  import NavbarContainer from "@/components/navbar/NavbarContainer.vue"
  import TimeoutModal from "@/components/TimeoutModal.vue"
  import Footer from "@/components/ui/footer/Footer.vue"

  import SoRewardingYouWonModal from "@/views/myaccount/loyalty/components/SoRewardingYouWonModal.vue"

  import { LayoutComponent, Layouts } from "./types/Layout"

  const mode = useColorMode()
  const route = useRoute()
  const router = useRouter()
  const queryClient = useQueryClient()
  const layoutComponent = computed(
    () =>
      (route.meta?.layout && LayoutComponent[route.meta?.layout]) ||
      LayoutComponent[Layouts.DEFAULT],
  )

  const { isAuthenticated } = authSession

  const { isSwitchAccountOpen, hasMultipleAccounts } = useSwitchAccount()

  watchEffect(() => {
    if (mode.value !== "light") mode.value = "light"
  })

  // So Energy's chat widget on every page but /myaccount/chat (the native app's chat page, which
  // sets up its own). An environment without a loader has no site-wide chat. The app mounts before
  // the first navigation resolves, so nothing is decided while the route is still START_LOCATION.
  if (isSoChatEnabled()) {
    const isChatInitialised = ref(false)
    watchEffect(() => {
      if (
        router.currentRoute.value !== START_LOCATION &&
        !isChatInitialised.value &&
        route.meta?.layout !== Layouts.CHAT
      ) {
        void initSoChat(router, queryClient)
        isChatInitialised.value = true
      }
    })
  }

  const { isErrorDialogShown } = useAuthErrorDialog()

  useAnalyticsTracking()
</script>

<template>
  <div class="flex min-h-full w-full flex-col">
    <div id="app-header" class="sticky top-0 z-50">
      <div id="debug-toolbar"></div>
      <NavbarContainer />
    </div>

    <component :is="layoutComponent">
      <RouterView :data-pagetitle="String($route?.meta?.title)" />
    </component>

    <Footer />
    <SwitchAccountModal
      v-if="hasMultipleAccounts"
      v-model:open="isSwitchAccountOpen"
    />
    <AuthErrorDialog
      v-if="isErrorDialogShown"
      @dialog-closed="isErrorDialogShown = false"
    />
    <TimeoutModal />
    <SoRewardingYouWonModal v-if="isAuthenticated" />
  </div>
</template>
